import { useTranslation } from 'next-i18next';
import { contains } from 'ramda';
import { useMemo } from 'react';
import { Box } from '@bridebook/ui';
import { IconReview } from '@bridebook/ui/src/icons';
import BeforeWellSectionWrap from '@bridebook/web/app-shared/components/supplier/sections/before-well-section-wrap/before-well-section-wrap';
import { REVIEWS_SECTION_ID } from '@bridebook/web/app-shared/components/supplier/sections/ids';
import AccordionCustom from '@bridebook/web/app-basic/components/supplier/sections/reviews/accordion-custom/accordion-custom';
import Review from '@bridebook/web/app-basic/components/supplier/sections/reviews/review';
import SupplierAccordion from '@bridebook/web/app-shared/components/supplier/sections/supplier-accordion/supplier-accordion';
import componentStyles from './reviews.style';
import { useSelector } from '@bridebook/web/app-basic/lib/utils';
import { getScreenType } from '@bridebook/web/app-basic/lib/app/selectors';
import { useSupplierAccordion } from '@bridebook/web/app-basic/components/supplier/hooks/use-supplier-accordion';
import {
  getFairs,
  getFeedbackCount,
  getIsVenue,
  getSupplierName,
  getSupplierReviews,
} from '@bridebook/web/app-basic/lib/supplier/selectors';
import { useReviewImageProps } from '@bridebook/web/app-basic/components/supplier/hooks/use-review-image';

const ReviewsSection = () => {
  const { t } = useTranslation('supplier');
  const { isMobile, isDesktop } = useSelector(getScreenType);
  const accordionProps = useSupplierAccordion();
  const isVenue = useSelector(getIsVenue);
  const feedbackCount = useSelector(getFeedbackCount);
  const reviews = useSelector(getSupplierReviews);
  const { feedback, hasReviews, hasTestimonials } = reviews;
  const totalTestimonials = useSelector(
    (state) => state.supplier.supplier?.counters?.testimonials || 0,
  );
  const fairs = useSelector(getFairs);
  const supplierName = useSelector(getSupplierName);

  const reviewImageProps = useReviewImageProps();

  const messageProps = { isDesktop, isVenue, trimText: false };

  const isWeddingFairs = Number(fairs?.length) > 0;
  const hasSomethingToShow = hasReviews || hasTestimonials;

  const leaveBorder = isMobile && contains('description-accordion', accordionProps.accordions);

  const styles = componentStyles({ hasSomethingToShow, isWeddingFairs });

  const text = useMemo(() => {
    if (isVenue) {
      return t('supplier.accordion.reviews.loggedOutVenueTitle', { supplierName });
    }

    return t('supplier.accordion.reviews.title');
  }, [isVenue, supplierName, t]);

  const variant = useMemo(() => {
    if (isVenue) return 'h2';
  }, [isVenue]);

  return (
    <Box id={REVIEWS_SECTION_ID} style={styles.testimonialsWrap}>
      <BeforeWellSectionWrap isBefore={isWeddingFairs}>
        <SupplierAccordion
          {...accordionProps}
          openStatic
          leaveBorder={leaveBorder}
          name="testimonials-accordion"
          id="supplier-reviews-section"
          icon={<IconReview color="space" width={20} />}
          text={text}
          itemAmount={feedbackCount}
          headCustom={
            !isVenue && (
              <AccordionCustom
                totalTestimonials={totalTestimonials}
                reviews={reviews}
                withAddReview={false}
              />
            )
          }
          variant={variant}
          horizontalMargins={isVenue && isMobile}
          hideArrow={!isMobile}>
          <Box style={styles.sectionWrap}>
            {hasSomethingToShow && (
              <Box style={styles.contentWrap}>
                <Box style={styles.listWrap}>
                  {feedback?.map((endorser) => (
                    <Box style={styles.reviewWrap} key={`endorser-${endorser.id}`}>
                      <Review
                        messageProps={messageProps}
                        endorser={endorser}
                        isMobile={isMobile}
                        reviewImageProps={reviewImageProps}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </SupplierAccordion>
      </BeforeWellSectionWrap>
    </Box>
  );
};

export default ReviewsSection;
